import React from 'react';
import { Box, Dialog, DialogContent, IconButton, styled } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ImageGallery from 'react-image-gallery';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { ArrowBack as ArrowBackIcon, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import 'react-image-gallery/styles/css/image-gallery.css';
import Utils from '../../utils';

const StyledGalleryWrapper = styled('div')(() => ({
    '& .image-gallery': {
        height: '100%',
    },
    '& .image-gallery-slide-wrapper': {
        height: '100%',
    },
    '& .image-gallery-content': {
        height: '100%',
    },
}));

const BootstrapDialog = styled(Dialog)(() => ({
    '& .MuiDialog-paper': {
        backgroundColor: '#fff',
        overflow: 'hidden',
        maxWidth: '90vw',
        maxHeight: '90vh',
        position: 'relative',
    },
    '& .MuiDialogContent-root': {
        padding: 0,
    },
}));

const MessageImageZoom = ({ open, onClose, image, images = [], startIndex = 0, initialRotation = 0 }) => {
    const [rotation, setRotation] = React.useState(initialRotation);
    const [currentImageUrl, setCurrentImageUrl] = React.useState(null);
    const hasGallery = Array.isArray(images) && images.length > 0;

    React.useEffect(() => {
        if (open) {
            setRotation(initialRotation);
            if (hasGallery && images.length > 0) {
                setCurrentImageUrl(images[startIndex] || images[0]);
            } else if (image) {
                setCurrentImageUrl(image);
            }
        }
    }, [open, initialRotation, hasGallery, images, image, startIndex]);

    const handleClose = () => {
        setRotation(0);
        onClose?.();
    };

    const rotateClockwise = () => setRotation((r) => (r + 90));
    const rotateCounterClockwise = () => setRotation((r) => (r - 90));

    const handleCopyImage = async () => {
        try {
            const imageUrl = currentImageUrl || image;
            if (!imageUrl) {
                console.error('No image URL available');
                return;
            }

            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = async () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);

                    canvas.toBlob(async (blob) => {
                        try {
                            await navigator.clipboard.write([
                                new ClipboardItem({ [blob.type]: blob })
                            ]);
                            console.log('Image copied to clipboard successfully');
                            Utils.showAlert(1, 'Image copied');
                        } catch (clipboardError) {
                            console.error('Clipboard API error:', clipboardError);
                        }
                    }, 'image/png');
                } catch (canvasError) {
                    console.error('Canvas error:', canvasError);
                }
            };

            img.onerror = () => {
                console.error('Failed to load image:', imageUrl);
            };

            img.src = imageUrl;
        } catch (error) {
            console.error('Failed to copy image:', error);
        }
    };

    const handleSlideChange = (index) => {
        if (hasGallery && imageList && imageList[index]) {
            setCurrentImageUrl(imageList[index]);
        }
    };

    const imageList = hasGallery ? images : (image ? [image] : []);

    const renderGalleryImage = React.useCallback(
        (item) => (
            <Box
                sx={{
                    position: 'relative',
                    width: '90vw',
                    height: '80vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fff',
                    overflow: 'hidden',
                }}
            >
                <TransformWrapper options={{ limitToBounds: true }}>
                    <TransformComponent
                        wrapperStyle={{ width: '100%', height: '100%', display: 'flex' }}
                        contentStyle={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <Box
                            component="img"
                            src={item.original}
                            alt="preview"
                            onContextMenu={(e) => {
                                e.stopPropagation();
                            }}
                            sx={{
                                width: 'auto',
                                height: 'auto',
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                transform: `rotate(${rotation}deg)`,
                                transformOrigin: 'center center',
                                transition: 'transform 0.3s ease',
                                backgroundColor: '#fff',
                                cursor: 'zoom-in',
                            }}
                        />
                    </TransformComponent>
                </TransformWrapper>
            </Box>
        ),
        [rotation]
    );

    return (
        <StyledGalleryWrapper>
            <BootstrapDialog open={open} onClose={handleClose}>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: '#000',
                        zIndex: 10,
                        backgroundColor: 'white',
                        height: 'var(--fs-hero)',
                        width: 'var(--fs-hero)',
                        '&:hover': { backgroundColor: '#000', color: '#fff' }
                    }}
                >
                    <CloseIcon style={{ fontSize: 'calc(var(--fs-sm)*2)' }} />
                </IconButton>

                <Box
                    sx={{
                        position: 'absolute',
                        top: 'calc(var(--sp-xxs)*2))',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: 'var(--sp-md2)',
                        zIndex: 10,
                    }}
                >
                    <IconButton
                        aria-label="rotate counterclockwise"
                        onClick={rotateCounterClockwise}
                        sx={{
                            color: '#000', backgroundColor: 'white', height: 'var(--fs-hero)',
                            width: 'var(--fs-hero)',
                            '&:hover': { backgroundColor: '#000', color: '#fff' }
                        }}
                    >
                        <RotateLeftIcon style={{ fontSize: 'calc(var(--fs-sm)*2)' }} />
                    </IconButton>
                    <IconButton
                        aria-label="rotate clockwise"
                        onClick={rotateClockwise}
                        sx={{
                            color: '#000', backgroundColor: 'white', height: 'var(--fs-hero)',
                            width: 'var(--fs-hero)',
                            '&:hover': { backgroundColor: '#000', color: '#fff' }
                        }}
                    >
                        <RotateRightIcon style={{ fontSize: 'calc(var(--fs-sm)*2)' }} />
                    </IconButton>
                    <IconButton
                        aria-label="copy image"
                        onClick={handleCopyImage}
                        sx={{
                            color: '#000', backgroundColor: 'white', height: 'var(--fs-hero)',
                            width: 'var(--fs-hero)',
                            '&:hover': { backgroundColor: '#000', color: '#fff' }
                        }}
                    >
                        <ContentCopyIcon style={{ fontSize: 'calc(var(--fs-sm)*2)' }} />
                    </IconButton>
                </Box>

                <DialogContent>
                    {hasGallery ? (
                        <ImageGallery
                            items={images.map((u) => ({ original: u, thumbnail: u }))}
                            startIndex={startIndex}
                            showThumbnails={false}
                            showFullscreenButton={false}
                            showPlayButton={false}
                            showNav={true}
                            onSlide={(index) => {
                                setRotation(0);
                                handleSlideChange(index);
                            }}
                            renderItem={renderGalleryImage}
                            renderLeftNav={(onClick, disabled) => (
                                <IconButton color='primary' onClick={onClick} disabled={disabled} style={{ position: 'absolute', bottom: '40%', left: 'var(--sp-xs)', zIndex: '999', borderRadius: 'calc(var(--sp-lg)*2.35849)', backgroundColor: 'white' }}>
                                    <ArrowBackIcon sx={{ fontSize: 'calc(var(--fs-sm)*2)' }} />
                                </IconButton>
                            )}
                            renderRightNav={(onClick, disabled) => (
                                <IconButton color='primary' onClick={onClick} disabled={disabled} style={{ position: 'absolute', bottom: '40%', right: 'var(--sp-xs)', zIndex: '999', borderRadius: 'calc(var(--sp-lg)*2.35849)', backgroundColor: 'white' }}>
                                    <ArrowForwardIcon sx={{ fontSize: 'calc(var(--fs-sm)*2)' }} />
                                </IconButton>
                            )}
                        />
                    ) : (
                        image && (
                            <ImageGallery
                                items={[{ original: image, thumbnail: image }]}
                                showThumbnails={false}
                                showFullscreenButton={false}
                                showPlayButton={false}
                                showNav={false}
                                renderItem={renderGalleryImage}
                            />
                        )
                    )}
                </DialogContent>
            </BootstrapDialog>
        </StyledGalleryWrapper>
    );
};

export default MessageImageZoom;
